import { Autocomplete, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useFormikContext } from "formik";
import clsx from "clsx";
import React from "react";
import { t } from "ttag";
import InfoHint from "../../../components/Tooltip/InfoHint";
import useStyles from "./SiteSetup.style";
import { getOptionLabel, isSameOption } from "./siteSetupForm";


export const SectionTitle = ({ children, action, className }: any) => {
  const classes: any = useStyles();
  return (
    <Grid item xs={12} className={clsx(classes.sectionTitle, className)}>
      <Typography variant="subtitle2" color="textSecondary" className={classes.sectionTitleText}>
        {children}
      </Typography>
      {action && <div className={classes.sectionTitleAction}>{action}</div>}
    </Grid>
  );
};

// Input helpers for the setup form sections; must be used inside the setup dialog's Formik form
const useSiteSetupInputs = () => {
  const classes: any = useStyles();
  const formik: any = useFormikContext<any>();
  const { values, errors, touched, handleChange, handleBlur, setFieldValue, setFieldTouched } = formik;

  // Helper text falls back to " " so each field keeps its message line and nothing shifts when an error shows
  const fieldError = (name: any): any => (touched[name] && errors[name]) || "";

  // A full-width row in a column
  const row = (content: any, key: any) => (
    <Grid item xs={12} key={key}>
      {content}
    </Grid>
  );

  // Two fields per row in a column
  const half = (content: any, key: any) => (
    <Grid item xs={6} key={key}>
      {content}
    </Grid>
  );

  // Three per row on wide screens, used by the Packages section
  const third = (content: any, key: any) => (
    <Grid item xs={12} sm={6} md={4} key={key}>
      {content}
    </Grid>
  );

  const renderText = (name: any, label: any, extra: any = {}) => (
    <TextField
      fullWidth
      margin="none"
      size="small"
      name={name}
      label={label}
      value={values[name] ?? ""}
      onChange={handleChange}
      onBlur={handleBlur}
      error={!!fieldError(name)}
      helperText={fieldError(name) || " "}
      FormHelperTextProps={{ className: classes.helperText }}
      className={classes.field}
      {...extra}
    />
  );

  const handleSelectChange = (name: any, onSelect?: any) => (event: any, option: any) => {
    const value = option?.value || "";
    setFieldValue(name, value);
    if (onSelect) {
      onSelect(value);
    }
  };

  const handleSelectBlur = (name: any) => () => {
    setFieldTouched(name, true);
  };

  const handleDateChange = (name: any) => (date: any) => {
    setFieldValue(name, date);
  };

  // Text box of a searchable dropdown
  const renderSelectInput = (name: any, label: any) => (params: any) => (
    <TextField
      {...params}
      label={label}
      margin="none"
      error={!!fieldError(name)}
      helperText={fieldError(name) || " "}
      FormHelperTextProps={{ className: classes.helperText }}
      className={classes.field}
    />
  );

  // Text box of a date picker; extra.hint shows under it when there's no error
  const renderDateInput = (name: any, extra: any) => (params: any) => (
    <TextField
      {...params}
      fullWidth
      margin="none"
      size="small"
      error={!!fieldError(name)}
      helperText={fieldError(name) || extra.hint || " "}
      FormHelperTextProps={{ className: classes.helperText }}
      className={classes.field}
    />
  );

  const renderSearchSelect = (name: any, label: any, options: any, onSelect?: any, disabled = false) => (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      value={options.find((option: any) => option.value === values[name]) || null}
      onChange={handleSelectChange(name, onSelect)}
      onBlur={handleSelectBlur(name)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isSameOption}
      renderInput={renderSelectInput(name, label)}
    />
  );

  // For the short enum lists
  const renderSelect = (name: any, label: any, options: any) =>
    renderText(name, label, {
      select: true,
      children: [
        <MenuItem key="none" value=""><em>{t`None`}</em></MenuItem>,
        ...options.map((option: any) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)
      ]
    });

  const renderDate = (name: any, label: any, extra: any = {}) => (
    <DatePicker
      label={label}
      value={values[name] ?? null}
      onChange={handleDateChange(name)}
      renderInput={renderDateInput(name, extra)}
      {...extra}
    />
  );

  // Label followed by an info hint, like coolremote-commercial
  const labelWithHint = (label: any, hint: any) => (
    <span className={classes.labelWithHint}>
      {label}
      <InfoHint title={hint} />
    </span>
  );

  return {
    formik,
    classes,
    row,
    half,
    third,
    renderText,
    renderSearchSelect,
    renderSelect,
    renderDate,
    labelWithHint
  };
};

export default useSiteSetupInputs;
